import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CommonFieldService} from '../../../../services/common/common-field.service';
import {Order} from '../../../../model/clearing/order';
import {InternalTradeOrderViewService} from '../../../../services/internal/internal-trade-order-view.service';
import {OrdersTradesTableRequest} from '../../../../model/clearing/orders-trades-table-request';
import {finalize, Observable, takeUntil, timer} from "rxjs";
import {ApiValidation} from "../../../../model/api/api-validation";
import {ClearingSelectionInfo} from "../../../../model/clearing/clearing-selection-info";
import {ClearingAllocationService} from "../../../../services/clearing/clearing-allocation.service";
import {ClearingAllocationTaskService} from "../../../../services/clearing/clearing-allocation-task.service";
import {ClearingDetailComponent} from "./clearing-detail/clearing-detail.component";
import {ThemeService} from "../../../../services/tools/theme.service";
import {TableColumnConfig} from "../../../../model/api/table-column-config";
import {ClearingOrderInfoService} from "../../../../services/clearing/clearing-order-info.service";
import {ClearingInfoLoadRequest} from "../../../../model/clearing/clearing-info-load-request";
import {OrderTableBaseComponent} from "../order/order-table-base.component";
import {MoveOrderFormComponent} from "../form/move-order-form/move-order-form.component";
import {AppConfigService} from "../../../../services/app-config.service";
import {WebTradeOrderView} from "../../../../model/internal/web-trade-order-view";
import {ClearingTableConfigService} from "../../../../services/clearing/clearing-table-config.service";
import {ClearingCookieService} from "../../../../services/clearing/clearing-cookie-service";
import {ClearingSelectionService} from "../../../../services/clearing/clearing-selection.service";
import {ClearingOrderClearingWsService} from "../../../../services/clearing/ws/clearing-order-clearing-ws.service";
import {ClearingTableSyncWsService} from "../../../../services/clearing/ws/clearing-table-sync-ws.service";

@Component({
  selector: 'app-clearing-order-clearing-table[userView]',
  templateUrl: './order-clearing-table.component.html',
  styleUrls: ['../clearing-table-base.component.scss', 'order-clearing-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    MessageService,
    ConfirmationService,
    TranslateService,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderClearingTableComponent extends OrderTableBaseComponent {

  static ACCEPTED_GIVEUP_STATE: string = 'ACCEPTED_GIVEUP';
  static REVERSAL_REQUESTED_STATE: string = 'REVERSAL_REQUESTED';
  static ERROR_STATE: string = 'ERROR';

  @ViewChild('clearingDetailDialog')
  clearingDetailDialog: ClearingDetailComponent;

  @ViewChild('moveOrderForm')
  moveOrderDialog: MoveOrderFormComponent;

  constructor(private el: ElementRef
    , cdref: ChangeDetectorRef,
              protected allocationService: ClearingAllocationService,
              allocationTaskService: ClearingAllocationTaskService,
              protected commonFieldService: CommonFieldService,
              protected userViewService: InternalTradeOrderViewService,
              appConfigService: AppConfigService,
              clearingOrderWsService: ClearingOrderClearingWsService,
              messageService: MessageService,
              protected confirmationService: ConfirmationService,
              translateService: TranslateService,
              public themeService: ThemeService,
              protected clearingOrderInfoService: ClearingOrderInfoService,
              public tableConfigService: ClearingTableConfigService,
              protected clearingCookieService: ClearingCookieService,
              protected clearingTableSyncWsService: ClearingTableSyncWsService,
              protected clearingSelectionService: ClearingSelectionService) {
    super(cdref,
      allocationService,
      allocationTaskService,
      commonFieldService,
      userViewService,
      appConfigService,
      clearingOrderWsService,
      messageService,
      confirmationService,
      translateService,
      themeService,
      tableConfigService,
      clearingCookieService,
      clearingTableSyncWsService,
      clearingSelectionService);
    this.origin = WebTradeOrderView.ORIGIN.ORDER_CLEARING;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public refreshTable() {
    super.refreshTable();
    if (!this.tableConfig) {
      return;
    }
    let configToAdjust = this.tableConfig.find(value => value.field === 'reference');
    if (configToAdjust) {
      configToAdjust.style = (record: any) => {
        const reference = this.getOrderReference(record);
        if (!this.orderReferenceColorMap[reference]) {
          if (this.themeService.isDarkTheme)
            this.orderReferenceColorMap[reference] = this.generateLightColorHex();
          else
            this.orderReferenceColorMap[reference] = this.generateDarkColorHex();
        }
        return 'color:' + this.orderReferenceColorMap[reference] + ' !important;';
      }
    }
    if (configToAdjust) {
      configToAdjust.mapId = (record: any) => this.getOrderReference(record);
    }
    configToAdjust = this.tableConfig.find(value => value.field === 'type');
    if (configToAdjust)
      configToAdjust.mapId = (record: any) => record.clearings && record.clearings.length > 0 && record.clearings[0].order
      && record.clearings[0].order.type ? record.clearings[0].order.type : '';
    configToAdjust = this.tableConfig.find(value => value.field === 'tradeType');
    if (configToAdjust)
      configToAdjust.mapId = (record: any) => record.type;
    configToAdjust = this.tableConfig.find(value => value.field === 'volume');
    if (configToAdjust)
      configToAdjust.mapId = (record: any) => {
        if (record.clearings && record.clearings.length > 0) {
          return record.clearings.map(value => value.volume).reduce((previousValue, currenValue) => previousValue + currenValue, 0);
        }
        return record.volume;
      }
    if (!this.tableConfig.find(value => value.field === 'reason')) {
      const reasonColumnConfig: TableColumnConfig = {
        field: 'reason', width: 250, notSortable: true, notResizable: true, showToolTip: true,
        filterConfig: {
          type: 'text',
          matchMode: 'contains',
        }
      };
      reasonColumnConfig.mapId = (record: any) => {
        let displayState = '';
        if (record.clearings && record.clearings.length > 0 && record.clearings[0].reason)
          displayState += record.clearings[0].reason;
        return displayState;
      };
      reasonColumnConfig.style = (record) => {
        if (record.clearings && record.clearings.length > 0 && record.clearings[0].state
          && record.clearingInfo && this.clearingStateColorMap[record.clearings[0].state]) {
          return 'color: var(--' + this.clearingStateColorMap[record.clearings[0].state].toLowerCase() + ') !important;';
        }
        return '';
      };
      this.tableConfig.splice(1, 0, reasonColumnConfig);
    }
    this.markForCheck();
  }

  protected refreshButtons() {
    this.headerButtonConfig = [
      {
        tooltip: this.getTranslation('page.' + this.transPartKey + '.table.zoom_clearing'),
        icon: 'fas fa-magnifying-glass-plus',
        class: ' p-button-info',
        command: () => this.showDetail(),
        enabled: () => this.showDetailEnabled()
      }];
    this.tableContextMenuConfig = [];
    if (!this.isReadOnly()) {
      const moveOrderItems = [{
        id: 'movetootherapsid',
        label: this.getTranslation('page.' + this.transPartKey + '.table.move_to_other_aps_id'),
        icon: 'fas fa-arrow-right-arrow-left',
        command: event => this.moveOrder()
      }, {
        label: this.getTranslation('page.' + this.transPartKey + '.table.move_to_other_order_ref'),
        icon: 'fas fa-arrow-right-arrow-left',
        command: event => this.moveOrder(true)
      }];
      const clearingManagementItems = [{
        label: this.getTranslation('page.' + this.transPartKey + '.table.cancel_pending_clearing'),
        icon: 'fas fa-ban',
        command: event => this.cancelPendingClearing()
      }, {
        label: this.getTranslation('page.' + this.transPartKey + '.table.resend_clearing'),
        icon: 'fas fa-share-from-square',
        command: event => this.resendClearing()
      },
        {
          separator: true,
        }, {
          label: this.getTranslation('page.' + this.transPartKey + '.table.delete_clearing_error'),
          icon: 'fas fa-trash-can',
          id: 'deleteClearingError',
          enabled: () => this.deleteClearingErrorEnabled(),
          command: event => this.deleteClearingError()
        }, {
          label: this.getTranslation('page.' + this.transPartKey + '.table.force_clear'),
          icon: 'fas fa-people-pulling',
          command: event => this.forceClear()
        }];

      const reversalManagementItems = [{
        id: 'reversalmanagementreqgurev',
        label: this.getTranslation('page.' + this.transPartKey + '.table.request_reversal'),
        icon: 'fas fa-play',
        command: event => this.requestReversal()
      }, {
        id: 'reversalmanagementcancelreqgurev',
        label: this.getTranslation('page.' + this.transPartKey + '.table.cancel_requested_reversal'),
        icon: 'fas fa-stop',
        command: event => this.cancelReversal()
      }];

      this.headerButtonConfig.push(
        {
          separator: true,
        },
        {
          tooltip: this.getTranslation('page.' + this.transPartKey + '.table.move_order'),
          icon: 'fas fa-arrow-right-arrow-left',
          class: ' p-button-success',
          id: 'moveorder',
          enabled: () => this.moveOrderEnabled(),
          items: moveOrderItems
        },);

      if (this.canDoReversal()) {
        this.headerButtonConfig.push(
          {
            separator: true,
          },
          {
            tooltip: this.getTranslation('page.' + this.transPartKey + '.table.reversal_management'),
            icon: 'fas fa-backward',
            class: ' p-button-warning',
            id: 'reversalmanagement',
            enabled: () => {
              return this.reversalManagementEnabled(OrderClearingTableComponent.ACCEPTED_GIVEUP_STATE, OrderClearingTableComponent.REVERSAL_REQUESTED_STATE)
            },
            items: reversalManagementItems
          });
      }
      if (this.isAdmin()) {
        this.headerButtonConfig.push(
          {
            tooltip: this.getTranslation('page.' + this.transPartKey + '.table.clearing_management'),
            icon: 'fas fa-people-arrows',
            class: ' p-button-help',
            id: 'clearingManagement',
            enabled: () => this.clearingManagementEnabled(),
            items: clearingManagementItems
          });
      }

      this.tableContextMenuConfig.push(
        {
          id: 'moveorder',
          label: this.getTranslation('page.' + this.transPartKey + '.table.move_order'),
          icon: 'fas fa-arrow-right-arrow-left',
          items: moveOrderItems
        });

      if (this.canDoReversal()) {
        this.tableContextMenuConfig.push(
          {
            separator: true,
          },
          {
            id: 'reversalmanagement',
            label: this.getTranslation('page.' + this.transPartKey + '.table.reversal_management'),
            icon: 'fas fa-backward',
            items: reversalManagementItems
          });
      }
      if (this.isAdmin()) {
        this.tableContextMenuConfig.push(
          {
            id: 'clearingManagement',
            label: this.getTranslation('page.' + this.transPartKey + '.table.clearing_management'),
            icon: 'fas fa-people-arrows',
            items: clearingManagementItems
          });
      }
      this.tableContextMenuConfig.push(
        {
          separator: true,
        });
    }
    this.tableContextMenuConfig.push(
      {
        id: 'showDetail',
        label: this.getTranslation('page.' + this.transPartKey + '.table.zoom_clearing'),
        icon: 'fas fa-magnifying-glass',
        command: () => this.showDetail()
      });
  }

  private moveOrder(manageOrderReference: boolean = false) {
    if (this.selectedIds.length > 0) {
      const currentSelectedIds = [...this.selectedIds];
      this.setLoading();
      // this.tableLoadingCount++;
      this.allocationService.getSignificantClearingOrders(currentSelectedIds).subscribe(validation => {
        if (validation.success) {
          const firstOrder: Order = validation.item[0];
          this.moveOrderDialog.open(currentSelectedIds, firstOrder.avgPriceGroupId,
            firstOrder.reference, manageOrderReference);
        } else {
          this.errorMessage(validation.message);
        }
        this.setNotLoading();
        // this.tableLoadingCount--;
      });
    }
  }

  requestReversal() {
    if (this.validState(OrderClearingTableComponent.ACCEPTED_GIVEUP_STATE)) {
      const currentSelectedIds = [...this.selectedIds];
      this.confirmCommand(() => {
          this.allocationService.requestReversalClearing(currentSelectedIds).subscribe(apiValidation => {
            this.checkAllocationTask(apiValidation);
          });
        }, 'page.' + this.transPartKey + '.table.request_reversal',
        'page.' + this.transPartKey + '.table.request_reversal_confirm');
    }
  }

  cancelReversal() {
    if (this.validState(OrderClearingTableComponent.REVERSAL_REQUESTED_STATE)) {
      const currentSelectedIds = [...this.selectedIds];
      this.confirmCommand(() => {
          this.allocationService.cancelReversalClearing(currentSelectedIds).subscribe(apiValidation => {
            this.checkAllocationTask(apiValidation);
          });
        }, 'page.' + this.transPartKey + '.table.cancel_requested_reversal',
        'page.' + this.transPartKey + '.table.cancel_requested_reversal_confirm');
    }
  }

  cancelPendingClearing() {
    const currentSelectedIds = [...this.selectedIds];
    this.confirmCommand(() => {
        this.allocationService.cancelPendingClearing(currentSelectedIds).subscribe(apiValidation => {
          this.checkAllocationTask(apiValidation);
        });
      }, 'page.' + this.transPartKey + '.table.cancel_pending_clearing',
      'page.' + this.transPartKey + '.table.cancel_pending_clearing_confirm');
  }

  resendClearing() {
    const currentSelectedIds = [...this.selectedIds];
    this.confirmCommand(() => {
        this.allocationService.resendClearing(currentSelectedIds).subscribe(apiValidation => {
          this.checkAllocationTask(apiValidation);
        });
      }, 'page.' + this.transPartKey + '.table.resend_clearing',
      'page.' + this.transPartKey + '.table.resend_clearing_confirm');
  }

  deleteClearingError() {
    const currentSelectedIds = [...this.selectedIds];
    this.confirmCommand(() => {
        this.allocationService.deleteClearingError(currentSelectedIds).subscribe(apiValidation => {
          this.checkAllocationTask(apiValidation);
        });
      }, 'page.' + this.transPartKey + '.table.delete_clearing_error',
      'page.' + this.transPartKey + '.table.delete_clearing_error_confirm');
  }

  forceClear() {
    const currentSelectedIds = [...this.selectedIds];
    this.confirmCommand(() => {
        this.allocationService.forceClear(currentSelectedIds).subscribe(apiValidation => {
          this.checkAllocationTask(apiValidation);
        });
      }, 'page.' + this.transPartKey + '.table.force_clear',
      'page.' + this.transPartKey + '.table.force_clear_confirm');
  }

  showDetail(): void {
    const currentSelectedIds = [...this.selectedIds];
    this.setLoading();
    this.allocationService.getSignificantTradesFromClearings(currentSelectedIds).pipe(
      takeUntil(this.destroy$),
      finalize(() => this.setNotLoading())
    ).subscribe(apiValidation => {
      if (apiValidation.success) {
        this.clearingDetailDialog.open(apiValidation.item[0]);
      } else {
        this.errorMessage(apiValidation.message);
      }
    });
  }

  private moveOrderEnabled() {
    if (this.selectedIds.length > 0 && this.currentClearingSelectionInfosByLeg.length > 0
      && this.selectedIds.length <= this.appConfigService.allocationHardLimitCount) {
      // let isAps = true;
      // this.currentClearingSelectionInfosByLeg.forEach(value => {
      //   if (value.apsGrpTotalVolume != value.totalVolume) isAps = false;
      // });
      return true;
    }
    return false;
  }

  private clearingManagementEnabled() {
    return this.selectedIds.length > 0 && this.currentClearingSelectionInfosByLeg.length > 0
      && this.selectedIds.length <= this.appConfigService.allocationHardLimitCount;
  }

  private deleteClearingErrorEnabled() {
    let hasAllError = true;
    if (this.selectedIds.length > this.appConfigService.allocationHardLimitCount) {
      return false;
    }
    this.currentClearingSelectionInfosByLeg.forEach(value => {
      if (value.state != OrderClearingTableComponent.ERROR_STATE) hasAllError = false;
    });
    return hasAllError;
  }

  prepareRequestBeforePost(requestToPrepare: OrdersTradesTableRequest): any {
    return super.prepareRequestBeforePost(requestToPrepare);
  }

  protected convertColumnIdToFieldName(columnId: string): string {
    let filedName = super.convertColumnIdToFieldName(columnId);
    if (columnId) {
      if ('displaystate' == columnId.toLowerCase()) {
        filedName = 'clItem.state';
      } else if ('reason' == columnId.toLowerCase()) {
        filedName = 'clItem.reason';
      }
    }
    return filedName;
  }

  onMoveOrderSubmit(event: any) {
    this.startCheckingAllocationTask(event.taskId, allocationtask => {
      setTimeout(() => {
        this.moveOrderDialog.openWithRecord(event.formRecord);
      }, 250);
    });
  }

  refreshContextMenu() {
    this.tableContextMenuConfig.forEach(menu => {
      if (menu.id === 'showDetail') {
        menu.disabled = !this.showDetailEnabled();
      } else if (menu.id === 'reversalmanagement') {
        menu.disabled = !this.reversalManagementEnabled(OrderClearingTableComponent.ACCEPTED_GIVEUP_STATE, OrderClearingTableComponent.REVERSAL_REQUESTED_STATE);
        if (!menu.disabled)
          menu.items.forEach(value => {
            if (value.id === 'reversalmanagementreqgurev')
              value.disabled = !this.validState(OrderClearingTableComponent.ACCEPTED_GIVEUP_STATE);
            else if (value.id === 'reversalmanagementcancelreqgurev')
              value.disabled = !this.validState(OrderClearingTableComponent.REVERSAL_REQUESTED_STATE);
          });
      } else if (menu.id === 'moveorder') {
        menu.disabled = !this.moveOrderEnabled();
      } else if (menu.id === 'clearingManagement') {
        menu.disabled = !this.clearingManagementEnabled();
        if (!menu.disabled)
          menu.items.forEach(value => {
            if (value.id === 'deleteClearingError')
              value.disabled = !this.deleteClearingErrorEnabled();
          });
      }
    });
    this.tableContextMenuConfig = [...this.tableContextMenuConfig];
    this.markForCheck();
  }

  rowDblClick() {
    if (this.showDetailEnabled()) this.showDetail();
  }

  protected getSelectionSummary(summaryRequest: ClearingInfoLoadRequest) {
    return this.clearingOrderInfoService.getSelectionSummaryTradeMode(summaryRequest);
  }

  protected getSelectionInfoLegs(request: ClearingInfoLoadRequest): Observable<ApiValidation<ClearingSelectionInfo[]>> {
    return this.clearingOrderInfoService.getSelectionInfoLegsTradeMode(request);
  }

}
